import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const QuotaRing = ({ acts, quota, testId = "kpi-quota-ring" }) => {
  const pct = quota ? (acts || 0) / quota : 0;
  const r = 22, c = 2 * Math.PI * r;
  const done = pct >= 1;
  return (
    <div className="relative w-14 h-14 shrink-0" data-testid={testId} title={`${Math.round(pct * 100)}% of quota`}>
      <svg viewBox="0 0 56 56" className="w-14 h-14 -rotate-90">
        <circle cx="28" cy="28" r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
        <circle cx="28" cy="28" r={r} fill="none" stroke={done ? "#16a34a" : "#0d9488"} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(pct, 1))} style={{ transition: "stroke-dashoffset 1s ease-out" }} />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center ${Math.round(pct * 100) >= 1000 ? "text-[9px]" : "text-[11px]"} font-bold ${done ? "text-green-600" : "text-navy"}`} data-testid={`${testId}-pct`}>
        {quota ? `${Math.round(pct * 100)}%` : "—"}
      </span>
    </div>
  );
};

const HEAD = {
  "Current Activations": "Current", "Pacing Activations": "Pacing", "Current Quota": "Quota",
  "Current Upgrades": "Current", "Pacing Upgrades": "Pacing", "Upgrade Goal": "Goal",
  "Current BYOD": "Current", "Pacing BYOD": "Pacing", "BYOD Sales %": "Sales %",
  "Current Tablets": "Current", "Current Tablet %": "Tablet %", "Tablets Quota": "Quota",
  "Current FWA": "Current", "Pacing FWA": "Pacing", "FWA Quota": "Quota",
  "Current AutoPay TA %": "TA %", "AutoPay Opportunities": "Opps", "AutoPay Quota": "Quota",
  "Current Top-Ups": "Current", "Pacing Top-Ups": "Pacing", "Prior Month Top-Ups": "Prior", "Top-Ups Quota": "Quota",
  "Bob Calls": "Current",
  "Current Rewards %": "Current", "Prior Rewards %": "Prior", "Rewards Quota": "Quota",
  "Current Zulu": "Zulu", "Zulu Acts": "Acts", "Prior Zulu %": "Prior %", "Zulu Quota": "Quota",
};

// one-row table from a card's detail line items: headers on top, values underneath
const autoTable = (details) => {
  const items = details.flatMap(([l, v, l2, v2]) => (l2 ? [[l, v], [l2, v2]] : [[l, v]]));
  return { cols: items.map(([l]) => HEAD[l] || l), rows: [{ id: "row", values: items }] };
};

const DetailTable = ({ table, base }) => {
  const cell = "px-[3px] xl:px-1.5 text-center tabular-nums whitespace-nowrap border-l first:border-l-0 border-slate-200";
  return (
    <div className="overflow-hidden mt-2 border-t border-slate-100 -mx-3 xl:mx-0">
    <table className="vz-compact w-full" data-testid={`${base}-details`}>
      <thead>
        <tr className="text-[10px] uppercase tracking-tight text-slate-400">
          {table.cols.map((c, i) => <th key={i} className={`${i === 0 && !c ? "text-left pr-1" : cell} font-medium py-1`}>{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((r) => (
          <tr key={r.id}>
            {r.label != null && <td className="py-0.5 pr-1 text-slate-500 whitespace-nowrap">{r.label}</td>}
            {r.values.map(([id, v], i) => (
              <td key={id} className={`${cell} py-0.5 ${i === 0 ? "font-bold text-navy" : "text-slate-600"}`} data-testid={`${base}-${slug(id)}`}>{v}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
};

export const KpiCard = ({ card, index = 0, loading = false, prefix = "kpi", children }) => {
  const base = `${prefix}-${slug(card.title)}`;
  const ringId = prefix === "kpi" && card.key === "acts" ? "kpi-quota-ring" : `${prefix}-${card.key}-ring`;
  return (
    <motion.div className="h-full" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (index % 4) * 0.1 }}>
      <Card className="border-slate-200 h-full" data-testid={base}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm text-slate-500 font-body">{card.title}</p>
              <p className="text-2xl font-heading font-bold text-navy mt-1" data-testid={`${base}-value`}>{loading ? "..." : card.value}</p>
            </div>
            {!card.noRing && <QuotaRing acts={card.ringCur} quota={card.ringQuota} testId={ringId} />}
          </div>
          {children && <div className="-mx-2.5 2xl:-mx-1">{children}</div>}
          {(card.detailTable || card.details) && <DetailTable table={card.detailTable || autoTable(card.details)} base={base} />}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const KpiGrid = ({ cards, testId, ...rest }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid={testId}>
    {cards.map((card, i) => <KpiCard key={card.key} card={card} index={i} {...rest} />)}
  </div>
);
